/**
 * Protected Route Component
 * Wraps routes that require specific permissions
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTenant } from '../../contexts/TenantContext';
import { PERMISSION_LEVELS } from '../../contexts/PermissionConstants';

/**
 * ProtectedRoute - Renders children only if user has required permission
 *
 * Also blocks render until TenantContext has resolved. Without this gate,
 * page useEffects fire before localStorage.selectedTenantId is set, so the
 * X-Tenant-ID header is missing from initial requests and the API silently
 * resolves to the user's primary-assigned tenant — which can serve a
 * different tenant's data than the UI is showing.
 *
 * @param {string} module - Module name
 * @param {string} level - Required permission level (default: read)
 * @param {ReactNode} children - Child components
 * @param {string} redirectTo - Redirect path if permission denied
 */
const ProtectedRoute = ({
  module,
  level = PERMISSION_LEVELS.READ,
  children,
  redirectTo = '/unauthorized'
}) => {
  const { hasPermission, loading: permLoading } = usePermissions();
  const { loading: tenantLoading, tenant } = useTenant();

  if (permLoading || tenantLoading || !tenant?.id) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        Loading...
      </div>
    );
  }

  const permitted = hasPermission(module, level);

  if (!permitted) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default ProtectedRoute;
